/// <amd-module name="VSS/Features/Markdown/Plugins/LazyHeaders" />
import * as MarkdownIt from "markdown-it";

/**
 * Interprets lazy headers, including #foo and #<space>foo
 * Based on markdown-it-lazy-headers, but correctly wraps it as requirejs dependency 
 */
export class LazyHeaders {

    constructor(md: MarkdownIt.MarkdownIt) {
        // Register the transformer with heading (since it overrides how that works) and all text rulesets
        (<any>md.block.ruler).at('heading',
            (state: any, startLine: number, endLine: number, silent: boolean) => {
                return this._headerTransformer(state, startLine, endLine, silent);
            },
            {
                alt: ['paragraph', 'reference', 'blockquote']
            }
        );
    }

    private _headerTransformer = (state: any, startLine: number, endLine: number, silent: boolean): boolean => {
        // Find initial position of the block in the state, using startLine for lookup
        // bMarks is quick lookup list of line begin offsets , and tShift is indents for each line
        let symbolPosition = state.bMarks[startLine] + state.tShift[startLine];
        // eMarks is line end offsets for fast jumps
        let maxPosition = state.eMarks[startLine];
        let symbolCode = state.src.charCodeAt(symbolPosition);
        const headerMarker = 0x23;

        // Find if line starts with a #, and quit right away if it doesn't
        if (symbolCode !== headerMarker || symbolPosition >= maxPosition) {
            return false;
        }

        // Count number of # symbols
        let headerLevel = 1;
        // Start check with second position
        symbolPosition++;
        symbolCode = state.src.charCodeAt(symbolPosition);
        while (symbolPosition < maxPosition && headerLevel <= 6 && symbolCode === headerMarker) {
            // If current position has symbol, increment header level, increment position and read next symbol
            headerLevel++;
            symbolPosition++;
            symbolCode = state.src.charCodeAt(symbolPosition);
        }

        // If header exceeds maximum header position, quit
        if (headerLevel > 6) {
            return false;
        }

        // If silent return true, but don't inject token - note that false is returned if we are no match, so other rules pick up the block
        if (silent) {
            return true;
        }

        // Remove trailing spaces and # symbols - they are allowed in markdown for prettyfying
        // Move position back using markdown-it helper
        maxPosition = state.skipCharsBack(maxPosition, 0x20, symbolPosition); // move back for spaces first
        const contentEnd = state.skipCharsBack(maxPosition, headerMarker, symbolPosition); // move back for # symbols
        // Now if we are left with single space, that means there was a "#foo #..." syntax, which matches standard
        // In which case we move max back to that position, as not to render the content after #foo
        if (contentEnd > symbolPosition && state.src.charCodeAt(contentEnd - 1) === 0x20) {
            maxPosition = contentEnd;
        }

        // Now we need to inject 3 new tokens, since we found the header

        // Move to next line in the state
        state.line = startLine + 1;
        const headerLevelMarkdown = Array(headerLevel + 1).join("#");

        // Inject a new header opening token, this duplicates markdown-it own logic (1 indicates opening)
        let token = state.push('heading_open', 'h' + headerLevel.toString(), 1);
        token.map = [startLine, state.line];
        token.markup = headerLevelMarkdown;

        // Create header content
        token = state.push('inline', '', 0);
        // Assign token to a source map, this maps token to specific lines
        token.map = [startLine, state.line];
        // Cut off all begining # symbols, and take the rest until the end of the line (marked by eMarks above)
        token.content = state.src.slice(symbolPosition, maxPosition).trim();
        token.children = [];

        // Create header closing token (-1 indicates closing)
        token = state.push('heading_close', 'h' + headerLevel.toString(), -1);
        token.markup = headerLevelMarkdown;

        // Return true meaning we did something to the block, and markdown shouldn't try to process it as a different type of block
        return true;
    }
}
